/* SSi website – Intensive Learning sub-pages (Welsh additions).
 *
 * Two components:
 *   IS_Welsh5Day  – two 2017 five-day bootcamps, six learners, before/after on tape
 *   IS_WelshTom   – Tom's 30-day Welsh experiment, June 2021, 26 video diary entries
 *
 * Both reuse shared helpers from IntensiveSubpages.jsx.
 */

import React from 'react';
import { IS_Crumb, IS_VideoCard, IS_Next, isStyles, IS_YT_WATCH, IS_YT_EMBED } from './IntensiveSubpages.jsx';

const YT_THUMB = id => `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;

/* =========================================================================
   WELSH 5-DAY  –  two bootcamps, 2017.
   Each end-of-course video is a before/after compilation of the three
   learners on that course – their day-one expectations, then the Welsh they
   could produce on camera five days later.
   ========================================================================= */

function IS_Welsh5Day() {
  const courses = [
    {
      key: 'may-2017',
      tag: 'May 2017 · Bootcamp 1',
      title: 'May 2017.',
      body: 'Three learners. Five days in the room. Same SaySomethingin method, same eight-hours-a-day shape – we filmed everyone on day one and again on day five and let the difference speak for itself.',
      id: 'UlRdBHzbHdY',
    },
    {
      key: 'oct-2017',
      tag: 'October 2017 · Bootcamp 2',
      title: 'October 2017.',
      body: 'Six months later, a second cohort of three learners. Same format, different people, same arc – this is the cleanest like-for-like we have of what five days of intensive Welsh actually produces.',
      id: '29hD3ZKwEqo',
    },
  ];

  return (
    <>
      <IS_Crumb/>

      <section style={{...isStyles.heroWrap, ...isStyles.heroAlt}} data-screen-label="Hero">
        <div style={isStyles.heroInner}>
          <div style={isStyles.heroTag}>Sprint × 2 · May & October 2017 · Welsh</div>
          <h1 style={isStyles.heroH1}>Five days of Welsh</h1>
          <p style={isStyles.heroLead}>The simplest test of the method we've ever filmed: six early stage learners, two five-day bootcamps, before-and-after on camera. No edits, no rehearsals, just learners doing their best on day one and day five.</p>
          <ul style={isStyles.metaList}>
            <li style={isStyles.metaItem}><strong>Format · </strong>5 days, ~8 hrs/day</li>
            <li style={isStyles.metaItem}><strong>Two cohorts · </strong>May 2017 · October 2017</li>
            <li style={isStyles.metaItem}><strong>Six learners · </strong>three per course</li>
          </ul>
        </div>
      </section>

      {/* The premise */}
      <section style={{...isStyles.proseWrap, paddingBottom:40}} data-screen-label="The premise">
        <div style={isStyles.proseInner}>
          <div style={isStyles.kicker}>The premise</div>
          <h2 style={isStyles.h2}>Day one: what do you hope to do? Day five: say it in Welsh</h2>
          <p style={isStyles.p}>The two 2017 bootcamps were our cleanest experiment. We put the camera on six learners on day one and asked each of them what they hoped to be able to do in Welsh after five days. The answers were honest, modest, and a bit nervous. Then we got to work.</p>
          <p style={isStyles.p}>Five days later, we put the camera back on. Same people. This time in Welsh. The videos below are those two recordings, one per cohort – everyone's day-one expectations, and then the conversations they could actually have, five days in.</p>
          <p style={isStyles.pEm}>We want you to see the reality of the experience.</p>
        </div>
      </section>

      {/* Two video features */}
      <section style={{...isStyles.recapWrap, paddingTop:40}} data-screen-label="Before and after, both cohorts">
        <div style={isStyles.recapInner}>
          <div style={isStyles.kicker}>The two end-of-course videos</div>
          <h2 style={isStyles.h2}>Before, and then after</h2>
          <p style={isStyles.p}>One video per cohort. Each starts with day-one expectations from all three learners and ends with the Welsh those same three learners can produce, on camera, five days later.</p>
          <div style={isStyles.recapGrid} className="is-part-grid">
            {courses.map(c => (
              <IS_VideoCard key={c.key}
                thumb={YT_THUMB(c.id)}
                ytId={c.id}
                title={c.title}
                body={c.body}
                label={c.tag.split(' · ')[1]}/>
            ))}
          </div>
        </div>
      </section>

      {/* Why this still matters */}
      <section style={{...isStyles.proseWrap, background:'var(--offWhite)'}} data-screen-label="Why this still matters">
        <div style={isStyles.proseInner}>
          <div style={isStyles.kicker}>Why we still point at these</div>
          <h2 style={isStyles.h2}>Nine years on, this is still the shortest answer to "does it work?"</h2>
          <p style={isStyles.p}>We've run a lot of intensives since 2017 – longer ones, fancier ones, with celebrities and TV crews. But these two bootcamps remain the most straightforward demonstration we have of what happens when three people push themselves to the limit for five days.</p>
          <p style={isStyles.p}>These are ordinary people willing to work hard and trust in the approach. They didn't have any special support (apart from Aran cheering them on) or any special advantages. They just committed.</p>
          <p style={isStyles.pEm}>The results aren’t magic, they’re just methodology. You could achieve exactly the same as they did.</p>
        </div>
      </section>

      <IS_Next prev={{title:'Ten days of Irish', sub:'Carraroe, September 2025', href:'/intensive-ireland'}}
               next={{title:'Aran\u2019s 75 days', sub:'Croatian, summer 2026', href:'/intensive-croatia'}}/>
    </>
  );
}

/* =========================================================================
   WELSH – TOM'S 30 DAYS  –  marathon, June 2021.
   Up to 2 hours a day, complete beginner, no real-world deadline beyond
   "let's see if our own new method actually works." 26 video diary entries
   out of 30 days; the S4C interview footage is still being tracked down.
   ========================================================================= */

function IS_WelshTom() {
  /*
   * Real mapping from Tom's #Welsh30For30 playlist. The playlist is 26 videos
   * but they don't map 1:1 to days:
   *   - one intro video, before day 1 ("Intro to Welsh30For30 experiment")
   *   - days 2 and 3 each have BOTH a "Learning" and a "Talking" session
   *   - days 19 + 20 are a single combined session
   *   - days 11, 22, 24, 26, 27, 29 are not on tape
   *   - day 30 is "Last Day" – a full day in Welsh (the closing test)
   * Net: 26 videos covering 24 of the 30 days, plus the intro.
   */
  const INTRO    = { id:'6jxc1ebxkaY', title:'Intro to Welsh30For30 experiment.' };
  const FINAL    = { id:'BrL0OaUHALM', day:30, title:'Full day in Welsh.', note:'The closing test.' };
  const MISSING  = [11, 22, 24, 26, 27, 29];

  // Each "slot" represents one day on the calendar.
  // .videos = entries we have for that day (0–2). .gap = true if not on tape.
  // "learning" = sitting with the lesson; "talking" = practising aloud.
  const slots = [
    { day:1,  videos: [{ id:'DJ6Z2C5K3mg', kind:'talking' }] },
    { day:2,  videos: [{ id:'CUwTR0OxJHc', kind:'learning' }, { id:'Pfb7XoLkCV0', kind:'talking', note:'Pt 2' }] },
    { day:3,  videos: [{ id:'gsADolmtces', kind:'learning' }, { id:'GfnkzakLFJQ', kind:'talking' }] },
    { day:4,  videos: [{ id:'L0U61IPCcy8', kind:'talking' }] },
    { day:5,  videos: [{ id:'-g570aByn5U', kind:'talking' }] },
    { day:6,  videos: [{ id:'KXSUGbRe9nM', kind:'talking' }] },
    { day:7,  videos: [{ id:'wrYtPJ7s76E', kind:'talking' }] },
    { day:8,  videos: [{ id:'RlpAn-tS0pM', kind:'talking' }] },
    { day:9,  videos: [{ id:'776QMqNFo-g', kind:'talking' }] },
    { day:10, videos: [{ id:'MYwdTOipegw', kind:'talking' }] },
    { day:11, gap:true },
    { day:12, videos: [{ id:'JGtj6CR2I7M', kind:'talking' }] },
    { day:13, videos: [{ id:'49xokKOsgxU', kind:'talking' }] },
    { day:14, videos: [{ id:'YnBsgKkdZPY', kind:'talking' }] },
    { day:15, videos: [{ id:'PoTV3mFzHDU', kind:'talking' }] },
    { day:16, videos: [{ id:'S8v_BN85gwM', kind:'talking' }] },
    { day:17, videos: [{ id:'aNiRS3f_jlg', kind:'talking' }] },
    { day:18, videos: [{ id:'W_bSFAEWk4g', kind:'talking' }] },
    { day:'19 & 20', videos: [{ id:'7RMmv-4tmIQ', kind:'talking', note:'Combined session' }] },
    { day:21, videos: [{ id:'QBX0BwHckvw', kind:'talking' }] },
    { day:22, gap:true },
    { day:23, videos: [{ id:'hr-6BKEYlPY', kind:'talking' }] },
    { day:24, gap:true },
    { day:25, videos: [{ id:'y5mAmEZqECQ', kind:'talking' }] },
    { day:26, gap:true },
    { day:27, gap:true },
    { day:28, videos: [{ id:'lJ6wSW0-Vl8', kind:'talking' }] },
    { day:29, gap:true },
    { day:30, videos: [{ id:FINAL.id, kind:'final', note:'Full day in Welsh' }] },
  ];

  const kindLabel = k => k === 'learning' ? 'Learning' : k === 'final' ? 'Closing test' : 'Talking';
  const kindAccent = k => k === 'learning' ? 'var(--gold)' : k === 'final' ? 'var(--brandRed)' : 'var(--fg-1)';

  // Three nicely-spaced reference points for the "compare" strip.
  const dayOne  = slots.find(s => s.day === 1).videos[0];
  const dayMid  = slots.find(s => s.day === 15).videos[0];
  const dayLast = slots.find(s => s.day === 30).videos[0];

  // Local style tweaks for the gap card and the kind-tag on a video card.
  const localStyles = {
    gapCard: { display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', textAlign:'center', background:'transparent', border:'1px dashed var(--border-strong)', borderRadius:14, padding:'40px 16px', color:'var(--grey)', minHeight:200 },
    gapDay:  { fontFamily:'var(--font-display)', fontWeight:700, fontSize:22, color:'var(--grey)', marginBottom:6, letterSpacing:'-.01em' },
    gapNote: { fontSize:13, color:'var(--grey)', letterSpacing:'.04em', textTransform:'uppercase', fontWeight:600 },
    kindTag: { position:'absolute', top:14, right:14, background:'rgba(0,0,0,.78)', color:'#fff', fontSize:11, fontWeight:700, padding:'4px 10px', borderRadius:8, letterSpacing:'.08em', textTransform:'uppercase' },
    kindTagFinal: { background:'var(--brandRed)' },
    kindTagLearning: { background:'var(--gold)', color:'#1a1004' },
  };

  return (
    <>
      <IS_Crumb/>

      <section style={{...isStyles.heroWrap, ...isStyles.heroAlt}} data-screen-label="Hero">
        <div style={isStyles.heroInner}>
          <div style={isStyles.heroTag}>Marathon · June 2021 · Welsh</div>
          <h1 style={isStyles.heroH1}>Tom’s 30 days of Welsh</h1>
          <p style={isStyles.heroLead}>Tom Cassidy – a complete beginner in Welsh, and one of Aran’s closest long-term collaborators at SSi – gave himself thirty days, a maximum of two hours per day, and the SaySomethingin Welsh course.</p>
          <p style={isStyles.heroLead}>What he achieved was extraordinary.</p>
          <ul style={isStyles.metaList}>
            <li style={isStyles.metaItem}><strong>Format · </strong>Up to 2 hours × 30 days</li>
            <li style={isStyles.metaItem}><strong>Starting point · </strong>Complete beginner</li>
            <li style={isStyles.metaItem}><strong>On tape · </strong>24 of the 30 days, in 26 sessions</li>
            <li style={isStyles.metaItem}><strong>Closing test · </strong>An interview, in Welsh, on S4C</li>
          </ul>
        </div>
      </section>

      {/* The premise */}
      <section style={{...isStyles.proseWrap, paddingBottom:40}} data-screen-label="The premise">
        <div style={isStyles.proseInner}>
          <div style={isStyles.kicker}>The premise</div>
          <h2 style={isStyles.h2}>Two hours a day. Thirty days.</h2>
          <p style={isStyles.p}>By June 2021 we’d been refining the SaySomethingin Method for over a decade. Tom had been co-creating with Aran for five years, and came on board as a company director – but he’d never sat down and put the Welsh course through its paces from beginning to end. So he set up a clean experiment: thirty days, no more than two hours a day, starting from absolutely nothing, doing exactly what we ask our learners to do.</p>
          <p style={isStyles.p}>There was no real-world deadline – no holiday, no filming, no wedding. But Tom did line up one test at the end, just to keep himself honest: an interview, in Welsh, on S4C. That meant a real Welsh-speaking presenter, a real camera, and no pre-arranged script to fall back on. If the method worked, the interview would too.</p>
          <p style={isStyles.pEm}>(We’re tracking down the broadcast footage of the interview itself – it’ll live in this page when we have it.)</p>
        </div>
      </section>

      {/* Intro / where to start */}
      <section style={isStyles.preWrap} data-screen-label="Intro">
        <div style={isStyles.preInner}>
          <div style={isStyles.preCard}>
            <div style={isStyles.preThumbWrap}>
              <iframe
                src={`${IS_YT_EMBED(INTRO.id)}&playsinline=1`}
                title="Tom's intro to the experiment"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
                loading="lazy"
                style={{position:'absolute', inset:0, width:'100%', height:'100%', border:0}}
              />
            </div>
            <div style={isStyles.preBody}>
              <div style={isStyles.kicker}>Before day one</div>
              <h2 style={isStyles.preH}>The intro: setting up the experiment</h2>
              <p style={isStyles.p}>Tom recorded a kickoff stream before he started – talking through why he’s doing this, what he hopes to find out, and what “two hours a day for thirty days” is actually going to look like.</p>
            </div>
          </div>
        </div>
      </section>

      {/* How a day was structured */}
      <section style={{...isStyles.proseWrap, paddingBottom:40}} data-screen-label="How a day worked">
        <div style={isStyles.proseInner}>
          <div style={isStyles.kicker}>How a day worked</div>
          <h2 style={isStyles.h2}>Two kinds of session: Learning, and Talking</h2>
          <p style={isStyles.p}>Tom was aiming to do two different things each day – about an hour or so in the app itself, and then a session with Aran where Tom tried to use as much Welsh as possible – in other words, learning in the app and then practising with Aran.</p>
          <p style={isStyles.p}>This is actually an important part of the approach – you don’t have to start having practice conversations from day 1, but everything you’ve learnt only really comes to life when you start putting it into real conversations.</p>
        </div>
      </section>

      {/* Full diary grid - 30 day slots */}
      <section style={{...isStyles.diaryWrap, paddingBottom:40}} data-screen-label="Full diary">
        <div style={isStyles.diaryInner}>
          <div style={isStyles.kicker}>The whole run</div>
          <h2 style={isStyles.h2}>Day by day, all thirty of them</h2>
          <p style={isStyles.p}>Here’s the whole story, from the laughter at the beginning through some occasional frustration and uncertainty until the final stages filmed on a visit to Aran when Tom stayed in Welsh for an entire day.</p>
          <div style={isStyles.diaryGrid} className="is-diary-grid">
            {slots.flatMap(s => {
              if (s.gap) {
                return [];
              }
              return s.videos.map((v, i) => {
                const tagBase = localStyles.kindTag;
                const tagExtra = v.kind === 'final' ? localStyles.kindTagFinal : null;
                return (
                  <div key={`${s.day}-${i}`} style={isStyles.diaryCard} className="is-diary-card">
                    <div style={isStyles.diaryThumbWrap}>
                      <iframe
                        src={`${IS_YT_EMBED(v.id)}&playsinline=1`}
                        title={`Day ${s.day} – ${kindLabel(v.kind)}`}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        referrerPolicy="strict-origin-when-cross-origin"
                        allowFullScreen
                        loading="lazy"
                        style={{position:'absolute', inset:0, width:'100%', height:'100%', border:0}}
                      />
                      <div style={{...isStyles.diaryDayBadge, zIndex:2, pointerEvents:'none'}}>Day {s.day}</div>
                      {v.kind === 'learning' ? null : <div style={{...tagBase, ...(tagExtra || {}), zIndex:2, pointerEvents:'none'}}>{kindLabel(v.kind)}</div>}
                    </div>
                    <div style={isStyles.diaryBodyV}>
                      <div style={{...isStyles.diaryTitle, fontSize:16}}>
                        Day {s.day} · {kindLabel(v.kind)}
                      </div>
                      {v.note ? <p style={isStyles.diaryP}>{v.note}</p> : null}
                    </div>
                  </div>
                );
              });
            })}
          </div>
        </div>
      </section>

      {/* Compare day 1 / mid / end */}
      <section style={{...isStyles.proseWrap, background:'var(--offWhite)', paddingTop:40}} data-screen-label="Compare">
        <div style={isStyles.proseInner}>
          <div style={isStyles.kicker}>If you’re short on time</div>
          <h2 style={isStyles.h2}>Three days, side by side</h2>
          <p style={isStyles.p}>The fastest way to feel what an hour or two a day does to a brand-new language: watch day one, the middle of the run, and the closing test.</p>
          <div style={isStyles.recapGrid} className="is-part-grid">
            <IS_VideoCard thumb={YT_THUMB(dayOne.id)}  ytId={dayOne.id}  label="Day 1"  title="The very first Talking session."  body="Cold start. The honest, awkward beginning."/>
            <IS_VideoCard thumb={YT_THUMB(dayMid.id)}  ytId={dayMid.id}  label="Day 15" title="Half-way through."                 body="Patterns settling, motivation tested, real sentences appearing."/>
            <IS_VideoCard thumb={YT_THUMB(dayLast.id)} ytId={dayLast.id} label="Day 30" title="A full day in Welsh."               body="The closing test – Tom’s last day of the experiment, spent entirely in the language."/>
          </div>
        </div>
      </section>

      {/* What it tested */}
      <section style={isStyles.proseWrap} data-screen-label="What it tested">
        <div style={isStyles.proseInner}>
          <div style={isStyles.kicker}>The most important result</div>
          <h2 style={isStyles.h2}>Tom’s long-term involvement</h2>
          <p style={isStyles.p}>Although Tom and Aran had been talking together for years (ever since Aran drove from Caernarfon to Cambridge to support Tom’s trial using the method to teach Portuguese to his sixth form students) this marked the real beginning of their work together.</p>
          <p style={isStyles.p}>Since this intensive test, Tom has become a central part of the SaySomethingin project, helping improve everything we do, transforming our app, building a Popty (Welsh for ‘oven’) tool to create courses in other languages and helping inspire Aran on a regular basis.</p>
          <p style={isStyles.pEm}>And all because, as Tom says himself, he really knows the method works – after going through it himself, he believes it’s the best approach for rapid language learning in the world.</p>
        </div>
      </section>

      <IS_Next prev={{title:'Aran\u2019s 75 days', sub:'Croatian, summer 2026', href:'/intensive-croatia'}} next={null}/>
    </>
  );
}

export { IS_Welsh5Day, IS_WelshTom };
